import React from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { filterParams } from "op2mise-react-widgets/OpDataGrid/utils";
import { formatAmount, orderStatus } from "components/campaigns/helpers/helpers";
import useStore from "components/campaigns/hooks/useCampaignStore";
import { getMarketsStationNotification } from "components/campaigns/requests";
import { OpDataGrid } from "op2mise-react-widgets";
import Button from "shared/components/button/Button";
import "../../campaigns.css";
import BootstrapSpinner from "shared/components/bootstrap-spinner/BootstrapSpinner";
import { ORDER_INTEGRATION } from "enums/OrderIntegrationEnum";

const NotifyAllMarketsModal = ({ onHide, onSelectContacts, selectedCampaign, setShowComposeEmailForm }) => {
    const { dateFormat } = useStore((state) => state);
    const [isFetchingStations, setIsFetchingStations] = React.useState(true);
    const [selectedStations, setSelectedStations] = React.useState([]);
    const [stations, setStations] = React.useState();

    const handleGetStations = async () => {
        try {
            setIsFetchingStations(true);
            const res = await getMarketsStationNotification(selectedCampaign.id);
            setStations(
                res.contactStations.map((contact, index) => ({
                    ...contact,
                    rowId: `${contact.stationAvailId}-${index}`,
                }))
            );
        } catch(error) {
            console.error(error);
        } finally {
            setIsFetchingStations(false);
        }
    };

    const onSelectionChanged = (selectedNodes) => {
        onSelectContacts(
            selectedNodes.map((o) => ({
                contactId: o.data.contactId,
                stationAvailId: o.data.stationAvailId,
            }))
        );
        setSelectedStations(selectedNodes);
    };

    // Op data grid settings
    const headers = [
        'marketName',
        'stationName',
        'networkName',
        'contactFirstName',
        'contactLastName',
        'contactEmail',
        'availsUploaded',
        'lastNotified',
    ];

    const displayLastSeen = (params) => params.data?.hasContact
        ? params.value
            ? moment(params.value).format(dateFormat)
            : null
        : params.node.group ? null : 'No active station contact found';
    const displayAvailsUploaded = (params) =>
        params.node.level === 0 ? '' : params.value ? 'Yes' : 'No';

    const columnDefs = React.useMemo(() => [
        { headerName: 'STATION/CABLE', field: 'marketName', rowGroup: true, hide: true },
        { headerName: 'NETWORK', field: 'networkName' },
        { headerName: 'CONTACT FIRST/GROUP NAME', field: 'contactFirstName' },
        { headerName: 'CONTACT LAST NAME', field: 'contactLastName' },
        { headerName: 'CONTACT EMAIL ADDRESS', field: 'contactEmail' },
        {
            headerName: 'AVAIL UPLOADED',
            field: 'availsUploaded',
            filterParams: {
                valueFormatter: displayAvailsUploaded,
            },
            valueFormatter: displayAvailsUploaded,
            tooltipValueGetter: displayAvailsUploaded,
        },
        {
            headerName: 'LAST NOTIFIED',
            field: 'lastNotified',
            filter: 'agDateColumnFilter',
            filterParams: filterParams,
            valueFormatter: displayLastSeen,
            tooltipValueGetter: displayLastSeen,
        },
    ], []);

    const autoGroupColumnDef = React.useMemo(() => {
        return {
            cellRendererParams: { suppressCount: true },
            field: 'stationName',
            headerName: 'STATION/CABLE',
            sortable: true,
            filter: true,
            sort: "asc",
            width: 280,
            minWidth: 130,
            tooltipValueGetter: (params) => {
                const { node, value } = params;
                const masterRow = node.level === 0;

                if (masterRow) {
                    // remove total value for stations from recieved data
                    const newMarketName = value.replace(/\s+\|\s+\d+$/, '');
                    // get selected rows in group
                    const selectedChildCount = node.allLeafChildren.filter((child) => child.isSelected()).length;
                    return  `${newMarketName} | ${selectedChildCount}/${node.allChildrenCount}`;
                }

                // return unformatted value if not master row
                return value;
            },
        }
    }, []);

    const rowDataBuilder = (data) => {
        const rows = data;
        const rowData = []; // to be returned;

        rows?.forEach((station) => {
            rowData.push(station);
        });
        return rowData;
    };

    const DataGridRowData = React.useMemo(() => {
        return rowDataBuilder(stations);
    }, [stations]);

    const rowSelection = React.useMemo(() => {
        return {
            mode: 'multiRow',
            checkboxes: true,
            headerCheckbox: true,
            selectAll: 'filtered',
            headerClass: 'hide-header-selection-label',
            isRowSelectable: (params) => {
                // status: New, Pending Review, Confirmed, Cancellation Request Sent
                const statuses = [0, 2, 3, 5];
                const noContact = !params.data?.hasContact;
                const isDirectIntegration = params.data.orderIntegrationTarget === ORDER_INTEGRATION.direct;
                const isWideOrbit = params.data?.orderPlatform === 'Wide Orbit';
                const disabled = !((statuses.includes(params.data?.orderStatus) && isWideOrbit && isDirectIntegration) || noContact);

                return disabled;
            },
            groupSelects: 'descendants', 
        };
    }, []);

    React.useEffect(() => {
        handleGetStations();
    }, [])
    
    return (
        <Modal centered dialogClassName={'market-stations-modal'} onHide={onHide} show={true}>
            <div className={'station-wrapper'}>
                {isFetchingStations ? (
                    <BootstrapSpinner />
                ) : (
                    <>
                        <div className="d-flex flex-column position-relative" style={{height: "calc(100% - 120px)"}}>
                            <p>Select station(s) to notify</p>
                            {stations && (
                                <OpDataGrid
                                    autoGroupColumnDef={autoGroupColumnDef}
                                    columns={columnDefs}
                                    customFooterElement={() => (
                                        <span style={{ color: '#656565', fontSize: '12px' }}>
                                            {selectedStations.length} Station(s) selected
                                        </span>
                                    )}
                                    customVerbiage={'No Stations/Cables Found'}
                                    defaultSort={{ field: 'stationName', sort: 'asc' }}
                                    gridHeightPreset={{ height: '100%'}}
                                    gridName={'mp-campaign-form-notification-selected-market-modal'}
                                    groupDefaultExpanded={0}
                                    onGridReady={(params) => {
                                        const nodesToSelect = [];
                                        
                                        params.api.forEachNode((node) => {
                                            if (node.selectable && !node.group) nodesToSelect.push(node);
                                        });
                                        
                                        params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
                                    }}
                                    onSelectionChanged={onSelectionChanged}
                                    resizable={true}
                                    rowId="rowId"
                                    rows={DataGridRowData}
                                    rowSelection={rowSelection}
                                    sharedColumnProperties={{
                                        cellStyle: { fields: ['programCount'], value: { textAlign: 'right' } },
                                        filter: {
                                            fields: headers.filter(name => !['createdDate', 'lastNotified', 'programCount'].includes(name)),
                                            value: true,
                                        },
                                        flex: { fields: headers, value: 1 },
                                        sortable: { fields: headers, value: true },
                                    }}
                                    showAlternativeRowHighlight={true}
                                    suppressRowClickSelection={true}
                                />
                            )}

                        </div>
                        <div className={'d-flex justify-content-end mt-auto'}>
                            <Button
                                defaultBtn={true}
                                onClick={onHide}
                                text={'Cancel'}
                            />
                            <Button
                                disabled={!selectedStations.length}
                                onClick={setShowComposeEmailForm}
                                text={'Next'}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default NotifyAllMarketsModal;